<template>
  <div class="editor-section">
    <div class="form-group">
      <label class="form-label required-label">影片檔案</label>
      <div class="video-upload-area">
        <!-- 已上傳：顯示縮圖 + 影片 URL -->
        <div v-if="modelValue.originalContentUrl" class="video-uploaded">
          <div class="video-preview-box">
            <img v-if="modelValue.previewImageUrl" :src="modelValue.previewImageUrl" alt="" class="video-thumb" />
            <div v-else class="video-thumb-placeholder">
              <i class="ri-video-line"></i>
            </div>
            <div class="play-overlay"><div class="play-circle"><i class="ri-play-fill"></i></div></div>
          </div>
          <div class="video-info">
            <span class="video-url" :title="modelValue.originalContentUrl">{{ shortUrl(modelValue.originalContentUrl) }}</span>
            <button type="button" class="btn-remove" @click="removeVideo">
              <i class="ri-delete-bin-line"></i> 移除影片
            </button>
          </div>
        </div>

        <!-- 未上傳：上傳區 -->
        <div v-else class="video-dropzone" @click="triggerFileInput">
          <i class="ri-upload-cloud-2-line"></i>
          <span v-if="!uploading">點擊上傳影片</span>
          <span v-else>上傳中 {{ uploadProgress }}%...</span>
          <small>MP4 格式，最大 200MB</small>
          <input
            ref="fileInput"
            type="file"
            accept="video/mp4"
            style="display:none"
            @change="handleFileSelect"
          />
        </div>
      </div>
    </div>

    <div class="form-group">
      <label class="form-label required-label">縮圖</label>
      <ImageUploader
        :modelValue="modelValue.previewImageUrl"
        @update:modelValue="update('previewImageUrl', $event)"
        :upload-fn="uploadVideoThumbProcessed"
        shape="card"
        placeholder="上傳影片縮圖"
        :crop-width="VIDEO_THUMB_W"
        :crop-height="VIDEO_THUMB_H"
        :crop-only-if-aspect-mismatch="true"
        :crop-output-max-mb="1"
        accept="image/jpeg,image/png"
        hint="僅支援 JPEG / PNG。比例與 16:9 不符時會開啟裁切視窗，請拖曳圖片調整要保留的區域；輸出 800×450px"
        size-hint="原始檔最大 20MB；輸出縮圖 ≤1MB（符合 LINE 預覽圖建議）"
        :max-size-mb="20"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import ImageUploader from '@/components/ImageUploader.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { resampleImageFileToJpeg } from '@/utils/imageResample.js';

/** 影片預覽縮圖：固定 16:9 輸出，置中 cover 裁切 */
const VIDEO_THUMB_W = 800;
const VIDEO_THUMB_H = 450;
const VIDEO_THUMB_MAX_BYTES = 1024 * 1024;

const props = defineProps({ modelValue: { type: Object, required: true } });
const emit = defineEmits(['update:modelValue']);

const fileInput = ref(null);
const uploading = ref(false);
const uploadProgress = ref(0);

async function uploadVideoThumbProcessed(file) {
  const processed = await resampleImageFileToJpeg(file, {
    maxEdge: VIDEO_THUMB_W,
    maxBytes: VIDEO_THUMB_MAX_BYTES
  });
  return lineMsgTemplateService.uploadImage(processed);
}

function update(key, val) {
  emit('update:modelValue', { ...props.modelValue, [key]: val });
}

function triggerFileInput() {
  if (uploading.value) return;
  fileInput.value?.click();
}

async function handleFileSelect(e) {
  const file = e.target.files?.[0];
  if (!file) return;

  if (file.type !== 'video/mp4') {
    alert('僅支援 MP4 格式的影片');
    return;
  }
  if (file.size > 200 * 1024 * 1024) {
    alert('影片大小不能超過 200MB');
    return;
  }

  uploading.value = true;
  uploadProgress.value = 0;

  try {
    // 預簽名 URL 直傳 S3（影片過大無法走 /upload API）
    const { fileUrl } = await lineMsgTemplateService.uploadVideo(file, (percent) => {
      uploadProgress.value = percent;
    });
    update('originalContentUrl', fileUrl);
  } catch (err) {
    console.error('影片上傳失敗:', err);
    alert('影片上傳失敗：' + (err?.message || '未知錯誤'));
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
    if (fileInput.value) fileInput.value.value = '';
  }
}

function removeVideo() {
  emit('update:modelValue', { ...props.modelValue, originalContentUrl: '', previewImageUrl: '' });
}

function shortUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = u.pathname;
    return path.length > 40 ? '...' + path.slice(-35) : path;
  } catch { return url.slice(-40); }
}
</script>

<style scoped>
.video-upload-area { margin-bottom: 4px; }

.video-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 32px 16px;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  cursor: pointer;
  transition: all .2s;
  color: #6b7280;
}
.video-dropzone:hover { border-color: #3b82f6; color: #3b82f6; }
.video-dropzone i { font-size: 36px; }
.video-dropzone span { font-size: 14px; font-weight: 500; }
.video-dropzone small { font-size: 11px; color: #9ca3af; }

.video-uploaded {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.video-preview-box {
  position: relative;
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #1f2937;
}
.video-thumb { width: 100%; height: 100%; object-fit: cover; }
.video-thumb-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; font-size: 28px;
}
.play-overlay {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
}
.play-circle {
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 16px;
}
.video-info { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.video-url {
  font-size: 12px; color: #6b7280; font-family: monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.btn-remove {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border: 1px solid #fecaca; border-radius: 6px;
  background: #fff; color: #ef4444; font-size: 12px; cursor: pointer;
  width: fit-content; transition: all .15s;
}
.btn-remove:hover { background: #fef2f2; border-color: #ef4444; }
</style>
